import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { StepTrail } from '@/shared/ui/StepTrail';

/* The design's three-step rail for lineage: Connection, Dataset, Lineage. The rail itself is the
 * shared `StepTrail`, so this and the Suggestions flow cannot come to disagree about what a finished
 * step is; this file supplies only the three steps' words. */

export type LineageStep = 1 | 2 | 3;

export function LineageSteps({
  step,
  connectionName,
  datasetName,
  onStep,
}: {
  readonly step: LineageStep;
  /** The chosen source's name, or `null` before one is chosen or while it is unread. */
  readonly connectionName: string | null;
  readonly datasetName: string | null;
  readonly onStep: (step: LineageStep) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <StepTrail
      label={t('lineage.steps.label')}
      current={step}
      onStep={(n) => {
        if (n === 1 || n === 2 || n === 3) onStep(n);
      }}
      steps={[
        {
          title: t('lineage.steps.connection'),
          sub: connectionName ?? t('lineage.steps.chooseSource'),
        },
        {
          title: t('lineage.steps.dataset'),
          sub: datasetName ?? (step > 1 ? t('lineage.steps.chooseTable') : '—'),
        },
        {
          title: t('lineage.steps.lineage'),
          sub: step === 3 ? t('lineage.steps.both') : '—',
        },
      ]}
    />
  );
}
